import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { doc, onSnapshot, setDoc, collection, addDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import {
    ShieldCheck, AlertTriangle, RefreshCw, Save,
    GraduationCap, UserCheck, Users, Calendar,
    Clock, CheckCircle, Receipt, BookOpen, Wifi, School, Settings,
    Infinity, Bell, Trash2, Megaphone
} from 'lucide-react';
import { useToast } from '../contexts/ToastContext';

interface ModuleConfig {
    name: string;
    description: string;
    key: string;
    icon: React.ElementType;
    hasLimit?: boolean; // modules that support a numeric cap
    limitLabel?: string;
}

interface ModuleState {
    enabled: boolean;
    limit?: number;
}

type ModulesMap = Record<string, ModuleState>;

const moduleList: ModuleConfig[] = [
    { key: 'Students',          name: 'Students Management',         description: 'Student registration, profiles, and academic logs.',         icon: GraduationCap,  hasLimit: true,  limitLabel: 'Max Students'   },
    { key: 'Teachers',          name: 'Teachers Directory',          description: 'Teacher roster, profiles, and departmental assignments.',    icon: UserCheck,      hasLimit: true,  limitLabel: 'Max Teachers'   },
    { key: 'Staff',             name: 'Staff Management',            description: 'Administration, clerks, and HR payroll access.',             icon: Users,          hasLimit: true,  limitLabel: 'Max Staff'      },
    { key: 'Management',        name: 'Campus Node Control',         description: 'Add regional branches, system sync configs, and global logs.',icon: School,         hasLimit: true,  limitLabel: 'Max Campuses'   },
    { key: 'Schedule',          name: 'Academic Schedule',           description: 'Classroom timeslots, periods, and staff calendars.',         icon: Calendar,       hasLimit: false                              },
    { key: 'Attendance',        name: 'Staff Attendance',            description: 'Administrative staff sign-ins, logs, and timing audits.',    icon: Clock,          hasLimit: false                              },
    { key: 'Student Attendance',name: 'Student Attendance Stream',   description: 'Terminal RFID streams for classroom attendance.',            icon: CheckCircle,    hasLimit: false                              },
    { key: 'Finance',           name: 'Financial Ledger & Fees',     description: 'Tuition payments, transaction logs, accounts, petty cash.',  icon: Receipt,        hasLimit: false                              },
    { key: 'Library',           name: 'Digital Library Catalogue',   description: 'Book checkouts, catalogue inventory, and overdue fines.',    icon: BookOpen,       hasLimit: false                              },
    { key: 'Device Configuration', name: 'Device Configuration & RFID', description: 'Security gateways, RFID scanners, and connection rates.', icon: Wifi,           hasLimit: false                              },
    { key: 'Settings',          name: 'General Settings',            description: 'Core settings panel access for school coordinators.',        icon: Settings,       hasLimit: false                              },
    { key: 'Notifications',     name: 'Notifications Board',         description: 'Broadcast notifications and system alert broadcasts.',        icon: Bell,           hasLimit: false                              },
];

const TenantAccess: React.FC = () => {
    const [modules, setModules] = useState<ModulesMap>({});
    const [pendingLimits, setPendingLimits] = useState<Record<string, string>>({}); // local edit buffer
    const [loading, setLoading] = useState(true);
    const [savingKey, setSavingKey] = useState<string | null>(null);
    const { showSuccess, showError } = useToast();

    // Broadcast states
    const [sysNotifications, setSysNotifications] = useState<any[]>([]);
    const [showNotifyModal, setShowNotifyModal] = useState(false);
    const [notifyForm, setNotifyForm] = useState({ title: '', description: '', type: 'info' });
    const [sendingNotification, setSendingNotification] = useState(false);

    // Listen to broadcasts
    useEffect(() => {
        const q = query(collection(db, 'notifications'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snap) => {
            const list: any[] = [];
            snap.forEach(doc => {
                list.push({ id: doc.id, ...doc.data() });
            });
            setSysNotifications(list);
        }, (error) => {
            console.error('Error fetching system notifications in TenantAccess:', error);
        });
        return () => unsubscribe();
    }, []);

    const handleBroadcastNotification = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!notifyForm.title.trim() || !notifyForm.description.trim()) {
            showError('Invalid Broadcast', 'Title and description cannot be empty.');
            return;
        }
        setSendingNotification(true);
        try {
            await addDoc(collection(db, 'notifications'), {
                title: notifyForm.title,
                description: notifyForm.description,
                type: notifyForm.type,
                createdAt: new Date().toISOString()
            });
            showSuccess('Broadcast Sent', 'System notification broadcasted to all users successfully.');
            setShowNotifyModal(false);
            setNotifyForm({ title: '', description: '', type: 'info' });
        } catch (error: any) {
            showError('Broadcast Failed', error.message || 'Failed to broadcast.');
        } finally {
            setSendingNotification(false);
        }
    };

    const handleDeleteNotification = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this broadcasted notification?')) return;
        try {
            await deleteDoc(doc(db, 'notifications', id));
            showSuccess('Broadcast Deleted', 'Notification has been removed.');
        } catch (error: any) {
            showError('Deletion Failed', error.message || 'Failed to delete notification.');
        }
    };

    useEffect(() => {
        const docRef = doc(db, 'tenantSettings', 'modules');
        const unsubscribe = onSnapshot(docRef, (snap) => {
            if (snap.exists()) {
                const raw = snap.data() as Record<string, any>;
                // Normalise: could be legacy bool OR new {enabled, limit} shape
                const normalised: ModulesMap = {};
                moduleList.forEach(m => {
                    const v = raw[m.key];
                    if (v === undefined || v === null) {
                        normalised[m.key] = { enabled: true, limit: 0 };
                    } else if (typeof v === 'boolean') {
                        normalised[m.key] = { enabled: v, limit: 0 };
                    } else {
                        normalised[m.key] = { enabled: v.enabled !== false, limit: v.limit ?? 0 };
                    }
                });
                setModules(normalised);

                // Prime pending limits buffer from Firestore
                const pending: Record<string, string> = {};
                moduleList.forEach(m => {
                    const v = raw[m.key];
                    const lim = typeof v === 'object' && v?.limit ? String(v.limit) : '';
                    pending[m.key] = lim;
                });
                setPendingLimits(pending);
            } else {
                // First-time init – all enabled, no limits
                const init: ModulesMap = {};
                const pending: Record<string, string> = {};
                moduleList.forEach(m => { init[m.key] = { enabled: true, limit: 0 }; pending[m.key] = ''; });
                setModules(init);
                setPendingLimits(pending);
            }
            setLoading(false);
        }, (error) => {
            console.error('Error fetching module configs:', error);
            showError('Sync Error', 'Failed to load tenant module configurations.');
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const buildPayload = (updated: ModulesMap) => {
        const payload: Record<string, ModuleState> = {};
        Object.entries(updated).forEach(([k, v]) => { payload[k] = v; });
        return payload;
    };

    const handleToggle = async (key: string) => {
        setSavingKey(key);
        try {
            const current = modules[key] ?? { enabled: true, limit: 0 };
            const updated: ModulesMap = {
                ...modules,
                [key]: { ...current, enabled: !current.enabled }
            };
            await setDoc(doc(db, 'tenantSettings', 'modules'), buildPayload(updated));
            showSuccess('Module Updated', `${key} has been ${!current.enabled ? 'activated' : 'deactivated'}.`);
        } catch (e: any) {
            showError('Update Failed', e.message || 'Failed to save.');
        } finally {
            setSavingKey(null);
        }
    };

    const handleSaveLimit = async (key: string) => {
        const raw = pendingLimits[key] ?? '';
        const parsed = raw === '' ? 0 : parseInt(raw, 10);
        if (raw !== '' && (isNaN(parsed) || parsed < 0)) {
            showError('Invalid Limit', 'Limit must be a positive number or empty for unlimited.');
            return;
        }
        setSavingKey(`limit-${key}`);
        try {
            const current = modules[key] ?? { enabled: true };
            const updated: ModulesMap = {
                ...modules,
                [key]: { ...current, limit: parsed }
            };
            await setDoc(doc(db, 'tenantSettings', 'modules'), buildPayload(updated));
            showSuccess('Limit Saved', parsed > 0 ? `${key} limit set to ${parsed}.` : `${key} limit removed (unlimited).`);
        } catch (e: any) {
            showError('Save Failed', e.message || 'Failed to save limit.');
        } finally {
            setSavingKey(null);
        }
    };

    return (
        <div className="space-y-6 text-slate-100 bg-slate-950 min-h-screen p-1">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center space-x-3">
                    <div className="p-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 shadow-[0_0_15px_rgba(16,185,129,0.3)]">
                        <ShieldCheck className="h-6 w-6 text-slate-950" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-slate-100 flex items-center gap-2">
                            Tenant Module Access
                            <span className="text-[10px] font-black uppercase bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 px-1.5 py-0.5 rounded tracking-widest">GATEWAY</span>
                        </h2>
                        <p className="text-xs text-slate-400">Orchestrate module visibility, toggle access, and set hard usage limits per tenant.</p>
                    </div>
                </div>
            </div>

            {/* Warning */}
            <div className="p-4 bg-slate-900/60 border border-amber-500/20 rounded-2xl flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                <div>
                    <h4 className="text-xs font-bold text-slate-200">Global Administration Notice</h4>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                        Deactivating a module instantly hides its navigation and blocks all access for standard school administrators.
                        Setting a limit prevents creating more records than specified — tenant sees a prompt to contact Dynamix Zone.
                        Super Administrators retain absolute access.
                    </p>
                </div>
            </div>

            {/* Table */}
            {loading ? (
                <div className="flex justify-center py-20">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-400"></div>
                </div>
            ) : (
                <div className="rounded-2xl border border-slate-800 overflow-hidden shadow-[0_4px_30px_rgba(0,0,0,0.4)]">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-slate-900 border-b border-slate-800">
                                <th className="px-5 py-3.5 text-left text-[10px] font-black uppercase tracking-widest text-slate-500">Module</th>
                                <th className="px-5 py-3.5 text-left text-[10px] font-black uppercase tracking-widest text-slate-500 hidden md:table-cell">Description</th>
                                <th className="px-5 py-3.5 text-center text-[10px] font-black uppercase tracking-widest text-slate-500">Status</th>
                                <th className="px-5 py-3.5 text-center text-[10px] font-black uppercase tracking-widest text-slate-500">Limit</th>
                                <th className="px-5 py-3.5 text-center text-[10px] font-black uppercase tracking-widest text-slate-500">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-900">
                            {moduleList.map((m) => {
                                const state = modules[m.key] ?? { enabled: true, limit: 0 };
                                const isActive = state.enabled;
                                const isTogglingSaving = savingKey === m.key;
                                const isLimitSaving = savingKey === `limit-${m.key}`;
                                const limitRaw = pendingLimits[m.key] ?? '';
                                const savedLimit = state.limit ?? 0;
                                const Icon = m.icon;

                                return (
                                    <tr
                                        key={m.key}
                                        className={`transition-colors duration-150 group ${isActive ? 'bg-slate-950 hover:bg-slate-900/60' : 'bg-slate-950/40 opacity-60 hover:opacity-80'}`}
                                    >
                                        {/* Module name + icon */}
                                        <td className="px-5 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className={`p-1.5 rounded-lg ${isActive ? 'bg-emerald-500/10 text-emerald-400' : 'bg-slate-800 text-slate-600'}`}>
                                                    <Icon className="h-4 w-4" />
                                                </div>
                                                <div>
                                                    <p className={`font-bold text-sm ${isActive ? 'text-slate-200' : 'text-slate-500'}`}>{m.name}</p>
                                                    <p className="text-[10px] font-mono text-slate-600">KEY: {m.key.toUpperCase()}</p>
                                                </div>
                                            </div>
                                        </td>

                                        {/* Description */}
                                        <td className="px-5 py-4 hidden md:table-cell">
                                            <p className="text-xs text-slate-500 leading-relaxed max-w-xs">{m.description}</p>
                                        </td>

                                        {/* Status badge */}
                                        <td className="px-5 py-4 text-center">
                                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                                                isActive
                                                    ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                                                    : 'bg-slate-900 text-slate-600 border border-slate-800'
                                            }`}>
                                                <span className={`h-1.5 w-1.5 rounded-full ${isActive ? 'bg-emerald-400 animate-pulse' : 'bg-slate-600'}`}></span>
                                                {isActive ? 'Active' : 'Offline'}
                                            </span>
                                        </td>

                                        {/* Limit input */}
                                        <td className="px-5 py-4 text-center">
                                            {m.hasLimit ? (
                                                <div className="flex items-center justify-center gap-2">
                                                    <div className="relative">
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            placeholder="∞"
                                                            value={limitRaw}
                                                            onChange={e => setPendingLimits(prev => ({ ...prev, [m.key]: e.target.value }))}
                                                            className="w-24 bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-center text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                                                        />
                                                    </div>
                                                    <button
                                                        onClick={() => handleSaveLimit(m.key)}
                                                        disabled={isLimitSaving}
                                                        title="Save limit"
                                                        className="p-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20 transition-colors disabled:opacity-40"
                                                    >
                                                        {isLimitSaving
                                                            ? <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                                                            : <Save className="h-3.5 w-3.5" />}
                                                    </button>
                                                    {savedLimit > 0 && (
                                                        <span className="text-[10px] text-emerald-400 font-mono">/{savedLimit}</span>
                                                    )}
                                                    {savedLimit === 0 && (
                                                        <Infinity className="h-3.5 w-3.5 text-slate-600" />
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-xs text-slate-700">—</span>
                                            )}
                                        </td>

                                        {/* Toggle button */}
                                        <td className="px-5 py-4 text-center">
                                            <button
                                                onClick={() => handleToggle(m.key)}
                                                disabled={isTogglingSaving}
                                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 focus:outline-none disabled:opacity-40 ${
                                                    isActive ? 'bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.4)]' : 'bg-slate-700'
                                                }`}
                                            >
                                                {isTogglingSaving ? (
                                                    <RefreshCw className="h-3 w-3 text-white animate-spin mx-auto" />
                                                ) : (
                                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-300 ${isActive ? 'translate-x-6' : 'translate-x-1'}`} />
                                                )}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* ── System Notifications Broadcast Section ──────────────── */}
            <div className="mt-8 bg-slate-950 rounded-2xl border border-slate-800 p-5 shadow-[0_4px_30px_rgba(0,0,0,0.4)] text-left">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 rounded-xl bg-amber-500/10 text-amber-400 border border-amber-500/20 shadow-[0_0_15px_rgba(245,158,11,0.15)]">
                            <Megaphone className="h-5 w-5" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-slate-100 flex items-center gap-2">
                                System Broadcast Alerts
                            </h3>
                            <p className="text-xs text-slate-400">Broadcast notification alerts directly to all user header menus in real-time.</p>
                        </div>
                    </div>
                    <button
                        onClick={() => setShowNotifyModal(true)}
                        className="flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-slate-950 text-xs font-black uppercase tracking-wider px-4 py-2.5 rounded-xl transition-all duration-200 shadow-[0_0_15px_rgba(16,185,129,0.2)] active:scale-95 self-start sm:self-auto"
                    >
                        <Bell className="h-4 w-4" />
                        Create Broadcast
                    </button>
                </div>

                {/* Notifications list */}
                <div className="space-y-3 max-h-[400px] overflow-y-auto custom-scrollbar pr-2">
                    {sysNotifications.length === 0 ? (
                        <div className="text-center py-12 text-slate-500 text-xs border border-dashed border-slate-800 rounded-xl">
                            No active system broadcasts. Create one above!
                        </div>
                    ) : (
                        sysNotifications.map((n) => {
                            const typeColor = 
                                n.type === 'warning' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' :
                                n.type === 'success' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                                'bg-blue-500/10 text-blue-400 border-blue-500/20';
                            
                            return (
                                <div key={n.id} className="p-4 bg-slate-900/40 border border-slate-800/80 rounded-xl flex items-start justify-between gap-4 group">
                                    <div className="flex gap-3 items-start">
                                        <div className={`p-2.5 rounded-lg border shrink-0 ${typeColor}`}>
                                            <Bell className="h-4 w-4" />
                                        </div>
                                        <div>
                                            <p className="font-bold text-slate-200 text-sm">{n.title}</p>
                                            <p className="text-xs text-slate-400 mt-1 leading-relaxed">{n.description || n.message}</p>
                                            <span className="text-[10px] text-slate-500 font-mono mt-2 block">
                                                Broadcasted: {n.createdAt ? new Date(n.createdAt).toLocaleString() : 'Just now'}
                                            </span>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => handleDeleteNotification(n.id)}
                                        className="p-2 rounded-xl text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-all active:scale-95 duration-200 shrink-0"
                                        title="Delete broadcast"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* Broadcast Creation Modal */}
            {showNotifyModal && (
                <div className="fixed inset-0 z-[100000] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md">
                    <div className="bg-slate-900 border border-slate-800 w-full max-w-md rounded-2xl p-6 shadow-2xl space-y-4 text-left">
                        <div className="flex items-center justify-between pb-2.5 border-b border-slate-800">
                            <h3 className="text-sm font-bold text-slate-200 flex items-center gap-2">
                                <Megaphone className="h-4 w-4 text-emerald-400" />
                                Send New Broadcast Alert
                            </h3>
                            <button
                                onClick={() => setShowNotifyModal(false)}
                                className="text-slate-500 hover:text-slate-300 font-bold"
                            >
                                ✕
                            </button>
                        </div>

                        <form onSubmit={handleBroadcastNotification} className="space-y-4 text-xs">
                            <div>
                                <label className="block text-slate-400 font-semibold mb-1.5">Alert Title</label>
                                <input
                                    type="text"
                                    placeholder="e.g. System Maintenance Update"
                                    value={notifyForm.title}
                                    onChange={e => setNotifyForm(prev => ({ ...prev, title: e.target.value }))}
                                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-slate-400 font-semibold mb-1.5">Alert Message (Description)</label>
                                <textarea
                                    placeholder="Provide detailed message here..."
                                    value={notifyForm.description}
                                    onChange={e => setNotifyForm(prev => ({ ...prev, description: e.target.value }))}
                                    rows={3}
                                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-slate-400 font-semibold mb-1.5">Alert Type (Icon Theme)</label>
                                <select
                                    value={notifyForm.type}
                                    onChange={e => setNotifyForm(prev => ({ ...prev, type: e.target.value }))}
                                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                                >
                                    <option value="info">Info (Blue Theme)</option>
                                    <option value="success">Success (Emerald Theme)</option>
                                    <option value="warning">Warning (Amber Theme)</option>
                                </select>
                            </div>

                            <div className="flex justify-end gap-2 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowNotifyModal(false)}
                                    className="px-4 py-2 rounded-xl bg-slate-800 text-slate-300 font-bold hover:bg-slate-700 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={sendingNotification}
                                    className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-black uppercase tracking-wider transition-colors disabled:opacity-50 flex items-center gap-1.5"
                                >
                                    {sendingNotification ? 'Sending...' : 'Broadcast'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TenantAccess;
